import { fromScreen } from '@doltech/core/lib/responsive/responsive.util';
import { IconCourseCourseClose2 } from '@doltech/icons/Course/IconCourseCourseClose2';
import { IconSystemV2CourseCheck2 } from '@doltech/icons/SystemV2/IconSystemV2CourseCheck2';
import { IconSystemV2CourseInfoFill } from '@doltech/icons/SystemV2/IconSystemV2CourseInfoFill';
import { IconSystemV2EssentialWarningFill } from '@doltech/icons/SystemV2/IconSystemV2EssentialWarningFill';
import { ButtonGeneral } from '@doltech/ui/lib/figma/Button/ButtonGeneral/ButtonGeneral';
import { ResponsiveTypography } from '@doltech/ui/lib/figma/Typography/v3/ResponsiveTypography';
import * as React from 'react';
import styled from 'styled-components';
import { KeyboardInstructionItem } from '../KeyboardInstructionItem/KeyboardInstructionItem';
import { DictationSentenceStatusEnum } from '@doltech/utils/lib/constants';

const ContentWrapper = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  align-items: center;
  gap: 6px;
`;

const IconWrapper = styled.div`
  height: 20px;
  display: flex;
  align-items: center;
`;

const Instruction = styled.div`
  display: grid;
  grid-auto-columns: minmax(0, 1fr);
  gap: 8px;

  .mac-text {
    display: none;
  }

  ${fromScreen(1024)} {
    .mac-text {
      display: initial;
    }
  }
`;

const TextIcon = styled.div`
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  gap: 8px;
`;

const Main = styled.div`
  padding: 0px 20px 20px;

  display: grid;
  align-items: center;
  gap: 20px;

  ${fromScreen(600)} {
    grid-template-columns: minmax(0, 1fr) max-content;
    gap: 12px;
  }
`;

const ActionWrapper = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 12px;

  ${fromScreen(600)} {
    grid-template-columns: minmax(0, max-content);
  }
`;

interface FooterQuizProps {
  isEnd?: boolean;
  isRedoDisabled?: boolean;
  isCheckDisabled?: boolean;
  status?: DictationSentenceStatusEnum;
  isChecking?: boolean;
  onCheck?: () => void;
  onRedo?: () => void;
  onNext?: () => void;
  onReOrder?: () => void;
  onSubmit?: () => void;
}

export const FooterQuiz = (props: FooterQuizProps) => {
  const {
    status = DictationSentenceStatusEnum.UNTOUCHED,
    isEnd,
    isChecking,
    onCheck,
    onRedo,
    onNext,
    onReOrder,
    onSubmit,
  } = props;

  switch (status) {
    case DictationSentenceStatusEnum.CORRECT:
      return (
        <Main className="footer-quiz">
          <TextIcon>
            <IconWrapper>
              <IconSystemV2CourseCheck2 color="green100" size={16} />
            </IconWrapper>
            <ResponsiveTypography.Paragraph variant="semi-bold/14-20" color="green100">
              Chính xác! Bạn làm tốt lắm!
            </ResponsiveTypography.Paragraph>
          </TextIcon>
          {isEnd ? (
            <ButtonGeneral type="primary" size="large" text="Kết thúc bài" onClick={onSubmit} />
          ) : (
            <ButtonGeneral type="primary" size="large" text="Tiếp theo" onClick={onNext} />
          )}
        </Main>
      );

    case DictationSentenceStatusEnum.IN_PROGRESS:
      return (
        <Main className="footer-quiz">
          <TextIcon>
            <IconWrapper>
              <IconCourseCourseClose2 color="red100" size={16} />
            </IconWrapper>
            <ResponsiveTypography.Paragraph variant="semi-bold/14-20" color="red100">
              Chưa đúng! Bạn xem gợi ý script bên dưới để làm lại nhé!
            </ResponsiveTypography.Paragraph>
          </TextIcon>
          <ActionWrapper>
            <ButtonGeneral type="primary" size="large" text="Làm lại" onClick={onRedo} />
          </ActionWrapper>
        </Main>
      );
    case DictationSentenceStatusEnum.WRONG_POSITION:
      return (
        <Main className="footer-quiz">
          <TextIcon>
            <IconWrapper>
              <IconSystemV2EssentialWarningFill color="yellow100" size={16} />
            </IconWrapper>
            <ResponsiveTypography.Paragraph variant="semi-bold/14-20" color="yellow120">
              Sai vị trí rồi, bạn sắp xếp lại nha!
            </ResponsiveTypography.Paragraph>
          </TextIcon>
          <ButtonGeneral type="primary" size="large" text="Sắp xếp lại" onClick={onReOrder} />
        </Main>
      );

    default:
      return isChecking ? (
        <Main className="student-footer-quiz">
          <TextIcon>
            <IconWrapper>
              <IconSystemV2EssentialWarningFill color="yellow100" size={16} />
            </IconWrapper>
            <ResponsiveTypography.Paragraph variant="semi-bold/14-20" color="yellow120">
              Bài chép thiếu từ chưa điền. Cố gắng hoàn thành nhé!
            </ResponsiveTypography.Paragraph>
          </TextIcon>
          <ActionWrapper>
            <ButtonGeneral type="primary" size="large" text="Làm lại" onClick={onRedo} />
          </ActionWrapper>
        </Main>
      ) : (
        <Main className="footer-quiz">
          <div>
            <TextIcon>
              <IconWrapper>
                <IconSystemV2CourseInfoFill color="black80" size={16} />
              </IconWrapper>
              <Instruction>
                <ContentWrapper>
                  <ResponsiveTypography.Paragraph variant="medium/14-20" color="black60">
                    Nhấn phím
                  </ResponsiveTypography.Paragraph>
                  <KeyboardInstructionItem text="Space" />
                  <ResponsiveTypography.Paragraph variant="medium/14-20" color="black60">
                    để qua từ.
                  </ResponsiveTypography.Paragraph>
                </ContentWrapper>
                <ResponsiveTypography.Paragraph
                  variant="medium/14-20"
                  color="black60"
                  className="mac-text"
                >
                  Chuyển qua <b>Unikey Eng</b> để tránh lỗi typing trên Macbook.
                </ResponsiveTypography.Paragraph>
              </Instruction>
            </TextIcon>
          </div>
          <ActionWrapper>
            <ButtonGeneral type="primary" size="large" text="Kiểm tra" onClick={onCheck} />
          </ActionWrapper>
        </Main>
      );
  }
};
